import { Component, Input, OnInit, inject } from "@angular/core";
import { FormsModule } from "@angular/forms";
import { MatTooltipModule } from "@angular/material/tooltip";
import { Form } from "@app/forms/models";
import { FormsDataService } from "@app/forms/services/forms-data.service";
import { Pages } from "@constants/pages/Pages";
import { ImageType } from "@enums/ImageType";
import "@methods/Array";
import { IdNameCombination } from "@models/generic/IdNameCombination";
import { TabModel } from "@models/generic/TabModels/TabModel";
import { FormPermissionModel } from "@models/permissions/FormPermissionModel";
import { TabService } from "@services/tabs/TabService";
import { ToastrService } from "ngx-toastr";
import { StandingDataType } from "@enums/StandingDataType";
import { CerrixPromptService } from "@app/shared/services/cerrix-prompt.service";
import { finalize } from "rxjs/operators";
import { firstValueFrom } from "rxjs";
import { DebounceInputEventDirective } from "@app/shared/directives/debounce-input-event.directive";
import { InputTooltipDirective } from "@app/shared/directives/input-tooltip.directive";
import { TranslatePipe, injectTranslation, provideTranslationScope } from "@app/shared/i18n/translation-scope";
import { scope } from "@app/forms/i18n/scope";

@Component({
    selector: "form-overview",
    templateUrl: "./form-overview.component.html",
    styleUrls: ["./form-overview.component.scss"],
    imports: [FormsModule, MatTooltipModule, DebounceInputEventDirective, InputTooltipDirective, TranslatePipe],
    viewProviders: [provideTranslationScope(scope)],
})
export class FormOverviewComponent implements OnInit {
    private _formDS = inject(FormsDataService);
    private toastr = inject(ToastrService);
    pages = inject(Pages);
    tabService = inject(TabService);
    private _promptService = inject(CerrixPromptService);
    private translation = injectTranslation(scope);

    @Input() categories: IdNameCombination[];
    @Input() cerrixTab: TabModel;
    @Input() permissions: FormPermissionModel;

    formCategories: { Category: string; Forms: Form[] }[];
    scopedCategories: { Category: string; Forms: Form[] }[];

    imageType = ImageType;
    filter = "";

    async ngOnInit() {
        const forms = await firstValueFrom(this._formDS.getFormOverview());
        this.formCategories = [];
        this.categories.forEach((c) => {
            const gForms = forms.filter((d) => d.Category === c.ID);
            if (gForms.length > 0) {
                this.formCategories.push({
                    Category: c.Name,
                    Forms: gForms,
                });
            }
        });
        this.filterForms();
    }

    async deleteForm(form: Form) {
        await this.translation.ready();
        this._promptService
            .confirm(
                this.translation.t("formOverview.deleteFormTitle"),
                this.translation.t("formOverview.deleteFormMessage", { title: form.Title })
            )
            .onConfirm()
            .subscribe(() => {
                const successStatuses: number[] = [200, 204];
                const prompt = this._promptService.loader(
                    this.translation.t("formOverview.deletingFormLoader")
                );

                this._formDS
                    .deleteForm(form.ID)
                    .pipe(finalize(() => prompt.close()))
                    .subscribe((x) => {
                        if (successStatuses.includes(x.status)) {
                            this.cerrixTab.refresh();
                            this.toastr.info(
                                this.translation.t("formOverview.formDeletedMessage"),
                                this.translation.t("formOverview.deleteToastTitle")
                            );
                        } else {
                            this.toastr.error(
                                this.translation.t("formOverview.formNotDeletedMessage"),
                                this.translation.t("formOverview.deleteToastTitle")
                            );
                        }
                    });
            });
    }

    openForm(form: Form) {
        if (form.CanViewResults) {
            this.tabService.generateTab(this.pages.FormOverview, form.ID);
        }
    }

    editForm(form: Form) {
        if (form.CanEdit) {
            this.tabService.generateTab(this.pages.FormDetail, form.ID, form.Title);
        }
    }

    async addNewForm() {
        await this.translation.ready();
        this.tabService.generateTab(
            this.pages.FormDetail,
            0,
            this.translation.t("formOverview.newFormTabName")
        );
    }

    async inviteForm(form: Form) {
        await this.translation.ready();
        const title = this.translation.t("formOverview.inviteTabName", { title: form.Title });
        this.tabService.generateTab(this.pages.FormInvite, form.ID, title);
    }

    filterForms() {
        this.scopedCategories = [];
        const keywords = this.filter
            ? this.filter
                  .split(" ")
                  .filter((x) => x && x.trim().length > 0)
                  .map((kw) => kw.toLowerCase())
            : [];

        if (keywords.length == 0) {
            this.scopedCategories = this.formCategories;
            return;
        }

        this.formCategories.forEach((group) => {
            const matchingForms = group.Forms.filter((f) =>
                keywords.some((kw) => f.Title.toLowerCase().indexOf(kw) >= 0)
            );
            if (matchingForms.any()) {
                this.scopedCategories.push({
                    Category: group.Category,
                    Forms: matchingForms,
                });
            }
        });
    }

    async toggleIsOpenForEntries(form: Form) {
        await this.translation.ready();
        const continueMessage = this.translation.t("formOverview.toggleOpenConfirmMessage", {
            state: form.IsOpenForEntries ? "open" : "closed",
        });

        this._promptService
            .confirmCustom({
                maxWidth: "350px",
                maxHeight: "275px",
                data: {
                    title: this.translation.t("formOverview.toggleOpenConfirmTitle"),
                    message: continueMessage,
                },
            })
            .onConfirm()
            .subscribe(() => {
                this._formDS
                    .toggleFormOpenForEntries(form, !form.IsOpenForEntries)
                    .subscribe(() => {
                        form.IsOpenForEntries = !form.IsOpenForEntries;

                        const message = this.translation.t("formOverview.toggleOpenSuccessMessage", {
                            state: form.IsOpenForEntries ? "open" : "closed",
                            title: form.Title,
                        });
                        this.toastr.success(
                            message,
                            this.translation.t("formOverview.toggleOpenSuccessTitle")
                        );
                    });
            });
    }

    openCategoriesSD() {
        this.tabService.generateTab(
            this.pages.StandingData,
            StandingDataType.FormCategory as number
        );
    }

    openGroupSD() {
        this.tabService.generateTab(this.pages.StandingData, StandingDataType.FormGroup as number);
    }
}
